import { subAccountsSwitch } from '@/sideNav/subAccountsSwitch'
import { defineAbilityForUser } from '@/sideNav/defineAbility'
import { generateSideNavSettingsConfig } from '@/sideNav/sideNavSettings'
import MembershipService, { merchantOrganizationTypeId } from '@/services/membership/membership.service'
import { getAuthService } from '@/auth/auth.service'
import { USER_ROLES } from '@/components/layouts/templates/AppTemplateMixin'

export const SIDENAVSETTINGS_LOCAL_STORAGE_KEY = 'shipbob:sideNavSettings'

export async function getUserDataForSideNav (store) {
  const user = store?.getters?.['user/getUser']
  const oneIdClaim = store?.getters?.['user/getOneIdClaim']
  const featureFlags = store?.getters?.featureFlags || {}
  const customerPreferences = store?.getters?.customerPreferences || {}
  const homeCountry = store?.getters?.['pricing/userHomeCountry']
  const externalUserInfo = store?.getters?.['user/getExternalIdInfo']
  const isMerchantOrg = oneIdClaim?.lorg_type === merchantOrganizationTypeId
  const subAccountSwitchingEnabled = isMerchantOrg && !!oneIdClaim?.org_atts?.external_type_id
  const loggedInOrganizationId = oneIdClaim?.Lorg
  let subAccountOrgs = []
  let loggedInOrganization
  let parentOrganization
  let canSwichSubAccounts = false

  if (subAccountSwitchingEnabled) {
    /*
     * If the user is an admin, we need to fetch their Logged in organization ONLY.
     * This should be done using the LOrg field to ensure that it handles impersonation
     *
     * If the user is not an admin, we need to get all the Merchant Orgs they have access to
     * This is done using their User ID
     *
     * Admins DO NOT have the ability to switch organizations
     */
    if (user?.role === USER_ROLES.Admin) {
      loggedInOrganization = await MembershipService.getOrganization(loggedInOrganizationId)
    } else {
      const idToken = await getAuthService().getTokenClaims('id_token')
      const response = await MembershipService.getMerchantOrganizations(idToken?.sub)

      if (response && response.success && response.data && Array.isArray(response.data)) {
        loggedInOrganization = response.data.find(org => org?.organization_id === loggedInOrganizationId)
        parentOrganization = loggedInOrganization

        if (loggedInOrganization) {
          canSwichSubAccounts = response.data.length > 1
          subAccountOrgs = response.data

          if (loggedInOrganization?.parent_organization_id) {
            parentOrganization = subAccountOrgs.find(org => org?.organization_id === loggedInOrganization.parent_organization_id)
          }
        }
      }
    }
  }

  return {
    featureFlags,
    customerPreferences,
    isUSCustomer: homeCountry?.countryCode === 'US',
    isWhiteLabelUser: externalUserInfo?.isPoweredBySubAccountUser,
    role: user?.role,
    isMerchantOrg,
    loggedInOrganization,
    parentOrganization,
    subAccountOrgs,
    canSwichSubAccounts,
  }
}

function filterSideNavItems (items, ability) {
  return items
    .map(item => {
      const filteredChildren = item.children
        ? filterSideNavItems(item.children, ability)
        : []

      if (item.type === 'subTitle' || item.type === 'title' || item.type === 'separator') {
        return {
          ...item,
          children: filteredChildren.length > 0 ? filteredChildren : []
        }
      }

      if (ability.can('view', item.name)) {
        return {
          ...item,
          children: filteredChildren.length > 0 ? filteredChildren : []
        }
      }
      return null
    })
    .filter(item => item !== null)
}

function createSideNavBody (userData, ability) {
  const baseSettings = generateSideNavSettingsConfig(userData)
  let sideNavBody = filterSideNavItems(baseSettings.body, ability)

  if (ability.can('view', 'Switch Subaccounts')) {
    const switchItems = subAccountsSwitch(userData, ability)
    sideNavBody = [
      ...switchItems,
      ...sideNavBody
    ]
  }
  return sideNavBody
}

export async function generateAndStoreSideNavSettings (store) {
  const userData = await getUserDataForSideNav(store)
  const sideNavSettings = getSideNavStructureForUser(userData)

  try {
    localStorage.setItem(SIDENAVSETTINGS_LOCAL_STORAGE_KEY, JSON.stringify(sideNavSettings))
    console.log('Successfully stored side nav settings in local storage')
    return sideNavSettings
  } catch (error) {
    console.error('Failed to save side nav settings to local storage:', error)
  }
  return null
}

export function getStoredSideNavSettings () {
  try {
    const storedSettings = localStorage.getItem(SIDENAVSETTINGS_LOCAL_STORAGE_KEY)
    if (storedSettings) {
      const parsedSettings = JSON.parse(storedSettings)
      if (parsedSettings && parsedSettings.body) {
        console.log('Retrieved side nav settings from local storage')
        return parsedSettings
      }
    }
  } catch (error) {
    console.error('Failed to read or parse side nav settings from local storage:', error)
  }
  return null
}

export function clearStoredSideNavSettings () {
  try {
    localStorage.removeItem(SIDENAVSETTINGS_LOCAL_STORAGE_KEY)
    console.log('Cleared side nav settings from local storage')
  } catch (error) {
    console.error('Failed to clear side nav settings from local storage:', error)
  }
}

export function getSideNavStructureForUser (userData) {
  const ability = defineAbilityForUser(userData)
  const body = createSideNavBody(userData, ability)
  return {
    body,
    footer: [USER_ROLES.ClientCompanyOrderAccess, USER_ROLES.ClientCompanyInventoryOrderAccess, USER_ROLES.ClientCompanyBillingRestriction].includes(userData?.role)
      ? [{
          name: 'Logout',
          icon: 'logout-icon',
          link: '/app/merchant/#/Logout',
          children: []
        }]
      : []
  }
}

export async function getSideNavSettings (store) {
  const storedSettings = getStoredSideNavSettings()

  if (storedSettings) {
    console.log('Using stored side nav settings')
    return storedSettings
  }

  return await generateAndStoreSideNavSettings(store)
}
